import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApp, api } from "../context/AppContext";
import toast from "react-hot-toast";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, user } = useApp();

  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [focusedIdx, setFocusedIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [devOtp, setDevOtp] = useState("");
  const [visible, setVisible] = useState(false);
  const [slideOut, setSlideOut] = useState(false);
  const [slideDir, setSlideDir] = useState(1);

  const otpRefs = useRef([]);
  const phoneRef = useRef(null);

  useEffect(() => {
    setTimeout(() => setVisible(true), 40);
  }, []);
  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);
  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer((n) => n - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  const otp = otpDigits.join("");

  const handlePhoneChange = (e) => {
    setPhone(e.target.value.replace(/\D/g, "").replace(/^91/, "").slice(0, 10));
    setError("");
  };

  const handleOtpKey = (idx, e) => {
    if (e.key === "Backspace") {
      if (otpDigits[idx]) {
        const n = [...otpDigits];
        n[idx] = "";
        setOtpDigits(n);
      } else if (idx > 0) otpRefs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowLeft" && idx > 0)
      otpRefs.current[idx - 1]?.focus();
    else if (e.key === "ArrowRight" && idx < 5)
      otpRefs.current[idx + 1]?.focus();
    else if (e.key === "Enter" && otp.length === 6) handleVerifyOtp();
  };

  const handleOtpInput = (idx, val) => {
    const ch = val.replace(/\D/g, "").slice(-1);
    const n = [...otpDigits];
    n[idx] = ch;
    setOtpDigits(n);
    setError("");
    if (ch && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const p = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!p) return;
    e.preventDefault();
    setOtpDigits(
      Array(6)
        .fill("")
        .map((_, i) => p[i] || ""),
    );
    otpRefs.current[Math.min(p.length, 5)]?.focus();
  };

  const transition = (dir, fn) => {
    setSlideDir(dir);
    setSlideOut(true);
    setTimeout(() => {
      fn();
      setSlideOut(false);
    }, 200);
  };

  const handleSendOtp = async () => {
    setError("");
    if (phone.length !== 10) {
      setError("Enter a valid 10-digit number.");
      return;
    }
    setLoading(true);
    try {
      const formatted = `+91${phone}`;
      const res = await api.post("/auth/send-otp", { phone: formatted });
      setPhone(formatted);
      setResendTimer(30);
      toast.success("OTP sent!");
      if (res.data.devOtp) {
        setDevOtp(res.data.devOtp);
        toast(`Dev OTP: ${res.data.devOtp}`, { icon: "🔑", duration: 10000 });
      }
      transition(1, () => {
        setStep("otp");
        setTimeout(() => otpRefs.current[0]?.focus(), 60);
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    if (otp.length !== 6) {
      setError("Enter all 6 digits.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/auth/verify-otp", { phone, otp });
      login(res.data.user, res.data.token);
      toast.success("Welcome to ProteinSpot! 💪");
      navigate(res.data.user.role === "admin" ? "/admin" : "/");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid OTP. Try again.");
      setOtpDigits(["", "", "", "", "", ""]);
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/resend-otp", { phone });
      setResendTimer(30);
      toast.success("New OTP sent!");
      if (res.data.devOtp) {
        setDevOtp(res.data.devOtp);
        toast(`Dev OTP: ${res.data.devOtp}`, { icon: "🔑", duration: 10000 });
      }
    } catch {
      setError("Failed to resend.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(160deg, #f7fdf9 0%, #edf7f1 50%, #f4fdf6 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        fontFamily: "'Roboto', sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* subtle background blobs */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `
          radial-gradient(ellipse 55% 45% at 8% 15%,  rgba(45,139,78,0.07) 0%, transparent 65%),
          radial-gradient(ellipse 45% 40% at 92% 85%, rgba(76,175,114,0.06) 0%, transparent 65%),
          radial-gradient(ellipse 35% 30% at 85% 8%,  rgba(255,179,71,0.04) 0%, transparent 60%)
        `,
        }}
      />

      {/* back */}
      <button
        onClick={() => navigate("/")}
        style={{
          position: "absolute",
          top: 20,
          left: 20,
          display: "flex",
          alignItems: "center",
          gap: 5,
          background: "none",
          border: "none",
          color: "#9ca3af",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          padding: "6px 10px",
          borderRadius: 8,
          transition: "color 0.15s",
          zIndex: 10,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#2D8B4E")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        Back
      </button>

      {/* logo */}
      <div
        style={{
          textAlign: "center",
          marginBottom: 28,
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(-14px)",
          transition: "all 0.5s cubic-bezier(0.34, 1.3, 0.64, 1)",
        }}
      >
        <div
          style={{
            animation: "bob 3.5s ease-in-out infinite",
            display: "inline-block",
            marginBottom: 10,
          }}
        >
          <img
            src="https://res.cloudinary.com/proteinspot/image/upload/v1774009667/logo-transparent_tsfxmf.png"
            alt="ProteinSpot"
            style={{
              width: 80,
              height: 80,
              objectFit: "contain",
              filter: "drop-shadow(0 4px 14px rgba(45,139,78,0.22))",
            }}
          />
        </div>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 900,
            color: "#0f2e18",
            letterSpacing: "-0.6px",
            margin: 0,
            lineHeight: 1,
          }}
        >
          Protein<span style={{ color: "#2D8B4E" }}>Spot</span>
        </h1>
        <p
          style={{
            color: "#a0a0a0",
            fontSize: 12,
            marginTop: 4,
            fontWeight: 500,
          }}
        >
          Fresh &amp; Natural Protein Foods
        </p>
      </div>

      {/* card */}
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          background: "#fff",
          borderRadius: 24,
          boxShadow:
            "0 2px 4px rgba(0,0,0,0.04), 0 12px 48px rgba(45,139,78,0.1), 0 1px 2px rgba(0,0,0,0.06)",
          border: "1px solid rgba(45,139,78,0.09)",
          overflow: "hidden",
          opacity: visible ? 1 : 0,
          transform: visible
            ? "translateY(0) scale(1)"
            : "translateY(20px) scale(0.97)",
          transition: "all 0.5s cubic-bezier(0.34, 1.2, 0.64, 1) 0.07s",
        }}
      >
        {/* top gradient bar */}
        <div
          style={{
            height: 3,
            background: "linear-gradient(90deg, #2D8B4E, #4CAF72, #a8e6b8)",
          }}
        />

        {/* step progress */}
        <div style={{ display: "flex", padding: "18px 28px 0", gap: 6 }}>
          {[0, 1].map((i) => (
            <div
              key={i}
              style={{
                height: 3,
                flex: 1,
                borderRadius: 2,
                background:
                  i === 0 || step === "otp"
                    ? "linear-gradient(90deg, #2D8B4E, #4CAF72)"
                    : "#f0f0f0",
                transition: "background 0.4s ease",
              }}
            />
          ))}
        </div>

        {/* sliding content */}
        <div style={{ overflow: "hidden" }}>
          <div
            style={{
              padding: "22px 28px 28px",
              transform: slideOut
                ? `translateX(${slideDir * -28}px)`
                : "translateX(0)",
              opacity: slideOut ? 0 : 1,
              transition: slideOut
                ? "all 0.16s ease"
                : "all 0.24s cubic-bezier(0.34,1.2,0.64,1)",
            }}
          >
            {/* ── PHONE ── */}
            {step === "phone" && (
              <>
                <h2
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: "#111827",
                    margin: "0 0 4px",
                    letterSpacing: "-0.3px",
                  }}
                >
                  Login or Sign up
                </h2>
                <p
                  style={{
                    fontSize: 13,
                    color: "#9ca3af",
                    margin: "0 0 22px",
                    fontWeight: 500,
                  }}
                >
                  We'll send a one-time code to verify
                </p>

                {error && <ErrorBox msg={error} />}

                <label style={LS}>Mobile Number</label>
                <div
                  style={{
                    display: "flex",
                    marginBottom: 20,
                    border: `1.5px solid ${error ? "#fca5a5" : phone.length === 10 ? "#2D8B4E" : "#e5e7eb"}`,
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "#fafafa",
                    transition: "border-color 0.2s, box-shadow 0.2s",
                    boxShadow:
                      phone.length === 10
                        ? "0 0 0 3px rgba(45,139,78,0.1)"
                        : "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "0 13px",
                      borderRight: "1.5px solid #e5e7eb",
                      background: "#f5f5f5",
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ fontSize: 17 }}>🇮🇳</span>
                    <span
                      style={{
                        fontWeight: 700,
                        color: "#374151",
                        fontSize: 14,
                      }}
                    >
                      +91
                    </span>
                  </div>
                  <input
                    ref={phoneRef}
                    type="tel"
                    inputMode="numeric"
                    value={phone}
                    onChange={handlePhoneChange}
                    onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                    placeholder="Enter mobile number"
                    maxLength={10}
                    autoComplete="tel"
                    style={{
                      flex: 1,
                      padding: "14px 12px",
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#111827",
                      background: "transparent",
                      border: "none",
                      outline: "none",
                      fontFamily: "monospace",
                      letterSpacing: "1px",
                    }}
                  />
                  {phone.length > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        paddingRight: 12,
                        color: phone.length === 10 ? "#2D8B4E" : "#d1d5db",
                        alignSelf: "center",
                        transition: "color 0.2s",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {phone.length}/10
                    </span>
                  )}
                </div>

                <GreenBtn
                  onClick={handleSendOtp}
                  disabled={loading || phone.length !== 10}
                >
                  {loading ? (
                    <>
                      <Spin /> Sending…
                    </>
                  ) : (
                    <>
                      Continue <Arr />
                    </>
                  )}
                </GreenBtn>

                <p
                  style={{
                    fontSize: 11,
                    color: "#c0c0c0",
                    textAlign: "center",
                    marginTop: 16,
                  }}
                >
                  By continuing you agree to our{" "}
                  <span
                    style={{
                      color: "#2D8B4E",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Terms
                  </span>{" "}
                  &amp;{" "}
                  <span
                    style={{
                      color: "#2D8B4E",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Privacy Policy
                  </span>
                </p>
              </>
            )}

            {/* ── OTP ── */}
            {step === "otp" && (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    marginBottom: 20,
                  }}
                >
                  <button
                    onClick={() =>
                      transition(-1, () => {
                        setStep("phone");
                        setOtpDigits(["", "", "", "", "", ""]);
                        setError("");
                        setDevOtp("");
                        setTimeout(() => phoneRef.current?.focus(), 60);
                      })
                    }
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      flexShrink: 0,
                      border: "1.5px solid #e5e7eb",
                      background: "#fafafa",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "#6b7280",
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "#2D8B4E";
                      e.currentTarget.style.color = "#2D8B4E";
                      e.currentTarget.style.background = "rgba(45,139,78,0.04)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "#e5e7eb";
                      e.currentTarget.style.color = "#6b7280";
                      e.currentTarget.style.background = "#fafafa";
                    }}
                  >
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M19 12H5M12 5l-7 7 7 7" />
                    </svg>
                  </button>
                  <div>
                    <h2
                      style={{
                        fontSize: 20,
                        fontWeight: 800,
                        color: "#111827",
                        letterSpacing: "-0.3px",
                        margin: 0,
                      }}
                    >
                      Enter OTP
                    </h2>
                    <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>
                      Sent to{" "}
                      <strong style={{ color: "#2D8B4E", fontWeight: 700 }}>
                        {phone}
                      </strong>
                    </p>
                  </div>
                </div>

                {error && <ErrorBox msg={error} />}

                {devOtp && (
                  <div
                    style={{
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: 10,
                      padding: "9px 12px",
                      marginBottom: 16,
                      fontSize: 13,
                      color: "#2563eb",
                      fontWeight: 600,
                    }}
                  >
                    🔑 Dev OTP:{" "}
                    <span
                      style={{
                        fontFamily: "monospace",
                        fontSize: 16,
                        letterSpacing: "3px",
                        fontWeight: 900,
                      }}
                    >
                      {devOtp}
                    </span>
                  </div>
                )}

                <label style={LS}>6-Digit Code</label>
                <div
                  style={{ display: "flex", gap: 8, marginBottom: 10 }}
                  onPaste={handleOtpPaste}
                >
                  {otpDigits.map((d, i) => {
                    const isFocused = focusedIdx === i;
                    const isFilled = !!d;
                    const hasErr = !!error;
                    return (
                      <div key={i} style={{ position: "relative", flex: 1 }}>
                        <div
                          style={{
                            height: 54,
                            borderRadius: 11,
                            border: `2px solid ${hasErr ? "#fca5a5" : isFilled ? "#2D8B4E" : isFocused ? "#4CAF72" : "#e5e7eb"}`,
                            background: isFilled
                              ? "rgba(45,139,78,0.05)"
                              : "#fafafa",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 22,
                            fontWeight: 900,
                            color: "#111827",
                            fontFamily: "monospace",
                            transition:
                              "all 0.17s cubic-bezier(0.34,1.4,0.64,1)",
                            transform: isFilled ? "scale(1.06)" : "scale(1)",
                            boxShadow: isFocused
                              ? "0 0 0 3px rgba(45,139,78,0.12)"
                              : isFilled
                                ? "0 2px 8px rgba(45,139,78,0.14)"
                                : "none",
                            pointerEvents: "none",
                          }}
                        >
                          {d ||
                            (isFocused ? (
                              <span
                                style={{
                                  width: 2,
                                  height: 20,
                                  background: "#2D8B4E",
                                  borderRadius: 1,
                                  animation: "blink 1s infinite",
                                }}
                              />
                            ) : (
                              <span
                                style={{
                                  width: 5,
                                  height: 5,
                                  borderRadius: "50%",
                                  background: "#e0e0e0",
                                }}
                              />
                            ))}
                        </div>
                        <input
                          ref={(el) => (otpRefs.current[i] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={d}
                          onChange={(e) => handleOtpInput(i, e.target.value)}
                          onKeyDown={(e) => handleOtpKey(i, e)}
                          onFocus={() => setFocusedIdx(i)}
                          onBlur={() => setFocusedIdx(null)}
                          autoComplete={i === 0 ? "one-time-code" : "off"}
                          style={{
                            position: "absolute",
                            inset: 0,
                            opacity: 0,
                            cursor: "text",
                            width: "100%",
                            height: "100%",
                            zIndex: 2,
                          }}
                        />
                      </div>
                    );
                  })}
                </div>

                {/* fill bar */}
                <div
                  style={{
                    height: 2,
                    background: "#f0f0f0",
                    borderRadius: 2,
                    marginBottom: 20,
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      borderRadius: 2,
                      width: `${(otp.length / 6) * 100}%`,
                      background: "linear-gradient(90deg, #2D8B4E, #4CAF72)",
                      transition: "width 0.18s ease",
                    }}
                  />
                </div>

                <GreenBtn
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? (
                    <>
                      <Spin /> Verifying…
                    </>
                  ) : (
                    <>
                      Verify &amp; Login <Arr />
                    </>
                  )}
                </GreenBtn>

                <div style={{ textAlign: "center", marginTop: 18 }}>
                  {resendTimer > 0 ? (
                    <p style={{ fontSize: 13, color: "#9ca3af" }}>
                      Resend in{" "}
                      <span
                        style={{
                          color: "#2D8B4E",
                          fontWeight: 800,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {resendTimer}s
                      </span>
                    </p>
                  ) : (
                    <button
                      onClick={handleResend}
                      disabled={loading}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: 13,
                        color: "#2D8B4E",
                        fontWeight: 700,
                        cursor: "pointer",
                        textDecoration: "underline",
                        textDecorationColor: "rgba(45,139,78,0.3)",
                        opacity: loading ? 0.5 : 1,
                        padding: 0,
                      }}
                    >
                      Didn't receive it? Resend OTP
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* trust badges */}
      <div
        style={{
          display: "flex",
          gap: 22,
          marginTop: 22,
          opacity: visible ? 1 : 0,
          transition: "opacity 0.5s ease 0.35s",
        }}
      >
        {["🔒 Secure", "📱 OTP Verified", "✅ Trusted"].map((b, i) => (
          <span
            key={i}
            style={{ fontSize: 11, fontWeight: 600, color: "#b8b8b8" }}
          >
            {b}
          </span>
        ))}
      </div>

      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes bob   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
        @keyframes shake {
          0%,100%{transform:translateX(0)}
          20%{transform:translateX(-5px)} 40%{transform:translateX(5px)}
          60%{transform:translateX(-3px)} 80%{transform:translateX(3px)}
        }
      `}</style>
    </div>
  );
}

/* shared label style */
const LS = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: "#9ca3af",
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  marginBottom: 8,
};

function ErrorBox({ msg }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "#fff5f5",
        border: "1px solid #fecaca",
        borderRadius: 10,
        padding: "9px 12px",
        marginBottom: 16,
        animation: "shake 0.35s ease",
      }}
    >
      <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
      <span style={{ fontSize: 13, color: "#dc2626", fontWeight: 600 }}>
        {msg}
      </span>
    </div>
  );
}

function GreenBtn({ onClick, disabled, children }) {
  const active = !disabled;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        padding: "14px",
        borderRadius: 13,
        border: "none",
        cursor: active ? "pointer" : "not-allowed",
        background: active
          ? "linear-gradient(135deg, #2D8B4E, #4CAF72)"
          : "#f0f0f0",
        color: active ? "#fff" : "#bbb",
        fontSize: 15,
        fontWeight: 800,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        transition: "all 0.25s ease",
        boxShadow: active ? "0 6px 22px rgba(45,139,78,0.32)" : "none",
        letterSpacing: "0.1px",
      }}
      onMouseEnter={(e) => {
        if (active) {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 10px 30px rgba(45,139,78,0.42)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = active
          ? "0 6px 22px rgba(45,139,78,0.32)"
          : "none";
      }}
    >
      {children}
    </button>
  );
}

function Spin() {
  return (
    <span
      style={{
        width: 17,
        height: 17,
        borderRadius: "50%",
        border: "2.5px solid rgba(255,255,255,0.3)",
        borderTopColor: "#fff",
        display: "inline-block",
        animation: "spin .65s linear infinite",
      }}
    />
  );
}

function Arr() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}
